// =============================================================================
// Zone Types
// =============================================================================

export type ZoneType = 'expedition' | 'inventory' | 'production' | 'quarantine' | 'calibration' | 'staging' | 'returns';

export interface Zone {
  id: string;
  code: string;
  name: string;
  zone_type: ZoneType;
  color?: string;
  description?: string;
  photo_storage_path?: string | null;
  pick_priority?: number;
  created_at: string;
  updated_at: string;
}

export interface ZonesResponse {
  zones: Zone[];
  total: number;
}

// =============================================================================
// Location Types
// =============================================================================

export type BinType = 'standard' | 'bulk' | 'calibration' | 'staging' | 'returns' | 'overflow';
export type LocationStatus = 'active' | 'full' | 'reserved' | 'maintenance' | 'inactive';

export interface Location {
  id: string;
  zone_id: string;
  bin_code: string;
  name?: string;
  bin_type: BinType;
  status: LocationStatus;
  capacity_units?: number;
  notes?: string;
  created_at: string;
  updated_at: string;
  // Joined zone info
  zone_code?: string;
  zone_name?: string;
  zone_color?: string;
  full_code?: string;
}

export interface LocationsResponse {
  locations: Location[];
  page_number: number;
  page_length: number;
  total: number;
}

export interface LocationSummary {
  zones: Array<{
    zone_id: string;
    zone_code: string;
    zone_name: string;
    zone_type: ZoneType;
    zone_color?: string;
    location_count: number;
    active_count: number;
  }>;
  totals: {
    total_zones: number;
    total_locations: number;
    active_locations: number;
  };
}

// =============================================================================
// Inventory Item Types
// =============================================================================

export type ItemStatus = 'valid' | 'verify' | 'test' | 'dev' | 'dead';

export interface InventoryItem {
  id: string;
  mongo_sku_id: string;
  sku_code: string;
  sku_name?: string;
  location_id: string;
  quantity_on_hand: number;
  quantity_reserved: number;
  quantity_available: number;
  reorder_point?: number;
  reorder_quantity?: number;
  lot_number?: string;
  expiration_date?: string;
  unit_cost?: number;
  unit_of_measure?: string;
  item_status: ItemStatus;
  last_counted_at?: string;
  created_at: string;
  updated_at: string;
  // Joined location info
  bin_code?: string;
  bin_type?: BinType;
  zone_id?: string;
  zone_code?: string;
  zone_name?: string;
  zone_color?: string;
  full_location_code?: string;
  is_low_stock?: boolean;
  // Preferred supplier
  preferred_supplier_id?: string;
  preferred_supplier_name?: string;
  // Optional SKU details from MongoDB
  sku_details?: {
    _id: string;
    sku: string;
    name?: { en?: string; fr?: string };
    manufacturer?: string;
    model?: string;
  };
}

export interface InventoryResponse {
  items: InventoryItem[];
  page_number: number;
  page_length: number;
  total: number;
}

export interface InventorySummary {
  totals: {
    total_items: number;
    total_quantity: number;
    total_reserved: number;
    total_available: number;
    total_value?: number;
    low_stock_count: number;
    unique_skus: number;
  };
  by_status: Array<{
    status: ItemStatus;
    count: number;
    quantity: number;
  }>;
  by_zone: Array<{
    zone_id: string;
    zone_code: string;
    zone_name: string;
    zone_color?: string;
    item_count: number;
    total_quantity: number;
  }>;
  low_stock_items: InventoryItem[];
}

// =============================================================================
// Serialized Item Types
// =============================================================================

export type SerializedItemStatus = 'in_stock' | 'reserved' | 'allocated' | 'shipped' | 'installed' | 'rma' | 'scrapped';
export type DeviceStatus = 'valid' | 'verify' | 'test' | 'dev' | 'dead';

export interface SerializedItem {
  id: string;
  serial_number: string;
  mac_address?: string;
  dev_id?: string;
  mongo_sku_id: string;
  sku_code: string;
  mongo_transmitter_id?: string;
  mongo_sensor_id?: string;
  inventory_item_id?: string;
  location_id: string;
  item_status: SerializedItemStatus;
  device_status?: DeviceStatus;
  calibration_due_date?: string;
  last_calibration_date?: string;
  manufacture_date?: string;
  firmware_version?: string;
  hardware_version?: string;
  work_order_id?: string;
  packing_slip_id?: string;
  notes?: string;
  created_at: string;
  updated_at: string;
  // Joined location info
  bin_code?: string;
  zone_code?: string;
  zone_name?: string;
  full_location_code?: string;
}

export interface SerializedItemsResponse {
  items: SerializedItem[];
  page_number: number;
  page_length: number;
  total: number;
}

export interface SerializedItemsSummary {
  totals: {
    total_items: number;
    unique_skus: number;
    in_stock_count: number;
    reserved_count: number;
    shipped_count: number;
    installed_count: number;
    rma_count: number;
    calibration_due_count: number;
  };
  by_item_status: Array<{
    status: SerializedItemStatus;
    count: number;
  }>;
  by_device_status: Array<{
    status: DeviceStatus;
    count: number;
  }>;
  by_zone: Array<{
    zone_id: string;
    zone_code: string;
    zone_name: string;
    zone_color?: string;
    item_count: number;
  }>;
  calibration_due_items: SerializedItem[];
  recent_items: SerializedItem[];
}

// =============================================================================
// Stock Movement Types
// =============================================================================

export type MovementType = 'receive' | 'transfer' | 'pick' | 'ship' | 'adjust' | 'return' | 'scrap' | 'production_complete' | 'status_change';
export type ReasonCode = 'cycle_count' | 'damage' | 'qc_fail' | 'qc_pass' | 'rma' | 'correction' | 'found' | 'lost' | 'expired' | 'production' | 'customer_return' | 'manual_add' | 'other';

export interface StockMovement {
  id: string;
  movement_type: MovementType;
  inventory_item_id?: string;
  serialized_item_id?: string;
  mongo_sku_id: string;
  sku_code: string;
  quantity: number;
  from_location_id?: string;
  to_location_id?: string;
  work_order_id?: string;
  packing_slip_id?: string;
  purchase_order_number?: string;
  reference_number?: string;
  reason_code?: ReasonCode;
  notes?: string;
  // Status change tracking (for status_change movements)
  previous_status?: DeviceStatus;
  new_status?: DeviceStatus;
  performed_by: string;
  performed_by_name?: string;
  performed_at: string;
  created_at: string;
}

export interface MovementsResponse {
  movements: StockMovement[];
  page_number: number;
  page_length: number;
  total: number;
}

export interface MovementSummary {
  by_type: Array<{
    movement_type: MovementType;
    count: number;
    total_quantity: number;
  }>;
  by_day: Array<{
    date: string;
    count: number;
    receives: number;
    transfers: number;
    adjustments: number;
  }>;
  recent_movements: StockMovement[];
  period: {
    from: string;
    to: string;
  };
}

// =============================================================================
// SKU Search Types (MongoDB legacy)
// =============================================================================

export interface MongoSkuSearchResult {
  _id: string;
  sku: string;
  name?: { en?: string; fr?: string };
  manufacturer?: string;
  model?: string;
  inventory_level?: number;
  type?: 'inventory' | 'non_inventory' | 'service';
}

// =============================================================================
// Input Types
// =============================================================================

export interface CreateZoneInput {
  code: string;
  name: string;
  zone_type?: ZoneType;
  color?: string;
  description?: string;
  pick_priority?: number;
}

export interface UpdateZoneInput {
  name?: string;
  zone_type?: ZoneType;
  color?: string;
  description?: string;
  pick_priority?: number;
}

export interface CreateLocationInput {
  zone_id: string;
  bin_code: string;
  name?: string;
  bin_type?: BinType;
  status?: LocationStatus;
  capacity_units?: number;
  notes?: string;
}

export interface UpdateLocationInput {
  bin_code?: string;
  name?: string;
  bin_type?: BinType;
  status?: LocationStatus;
  capacity_units?: number;
  notes?: string;
}

export interface CreateInventoryInput {
  mongo_sku_id: string;
  sku_code: string;
  sku_name?: string;
  location_id: string;
  quantity_on_hand?: number;
  reorder_point?: number;
  reorder_quantity?: number;
  lot_number?: string;
  expiration_date?: string;
  unit_cost?: number;
  item_status?: ItemStatus;
}

export interface UpdateInventoryInput {
  sku_name?: string;
  location_id?: string;
  reorder_point?: number;
  reorder_quantity?: number;
  lot_number?: string;
  expiration_date?: string;
  unit_cost?: number;
  unit_of_measure?: string | null;
  item_status?: ItemStatus;
  preferred_supplier_id?: string | null;
  preferred_supplier_name?: string | null;
}

export interface ReceiveInventoryInput {
  location_id: string;
  mongo_sku_id: string;
  sku_code: string;
  sku_name?: string;
  quantity: number;
  lot_number?: string;
  expiration_date?: string;
  unit_cost?: number;
  item_status?: ItemStatus;
  purchase_order_number?: string;
  reference_number?: string;
  notes?: string;
}

export interface TransferInventoryInput {
  inventory_item_id: string;
  to_location_id: string;
  quantity: number;
  reference_number?: string;
  notes?: string;
}

export interface AdjustInventoryInput {
  inventory_item_id: string;
  new_quantity: number;
  reason_code: ReasonCode;
  reference_number?: string;
  notes?: string;
}

export interface CreateManualInventoryInput {
  name: string;
  location_id: string;
  quantity: number;
  mongo_sku_id?: string;
  create_new_sku?: boolean;
  sku_code?: string;
  supplier_name?: string;
  lot_number?: string;
  expiration_date?: string;
  unit_cost?: number;
  unit_of_measure?: string;
  notes?: string;
}

export interface ExistingInventoryCheck {
  id: string;
  mongoSkuId: string;
  skuCode: string;
  skuName?: string;
  locationId: string;
  quantityOnHand: number;
  quantityReserved: number;
  unitOfMeasure?: string;
  unitCost?: number | string;
  lotNumber?: string;
  itemStatus: string;
  binCode?: string;
  zoneName?: string;
}

export interface CreateSerializedItemInput {
  serial_number: string;
  mongo_sku_id: string;
  sku_code: string;
  location_id: string;
  mac_address?: string;
  dev_id?: string;
  device_status?: DeviceStatus;
  calibration_due_date?: string;
  firmware_version?: string;
  hardware_version?: string;
  notes?: string;
}

export interface UpdateSerializedItemInput {
  mac_address?: string;
  dev_id?: string;
  device_status?: DeviceStatus;
  calibration_due_date?: string;
  last_calibration_date?: string;
  firmware_version?: string;
  hardware_version?: string;
  notes?: string;
}

export interface ChangeDeviceStatusInput {
  device_status: DeviceStatus;
  reason: ReasonCode;
  notes?: string;
}

// =============================================================================
// Filter Types
// =============================================================================

export interface InventoryFilters {
  location_id?: string;
  zone_id?: string;
  mongo_sku_id?: string;
  sku_code?: string;
  preferred_supplier_id?: string;
  item_status?: ItemStatus;
  low_stock?: boolean;
  search?: string;
  page_number?: number;
  page_length?: number;
  sort_key?: string;
  sort_direction?: 1 | -1;
}

export interface SerializedItemFilters {
  location_id?: string;
  zone_id?: string;
  mongo_sku_id?: string;
  item_status?: SerializedItemStatus;
  device_status?: DeviceStatus;
  search?: string;
  page_number?: number;
  page_length?: number;
}

export interface MovementFilters {
  movement_type?: MovementType;
  mongo_sku_id?: string;
  inventory_item_id?: string;
  serialized_item_id?: string;
  from_location_id?: string;
  to_location_id?: string;
  performed_by?: string;
  reason_code?: ReasonCode;
  from_date?: string;
  to_date?: string;
  search?: string;
  page_number?: number;
  page_length?: number;
}

// =============================================================================
// Work Order Types
// =============================================================================

export type WorkOrderStatus = 'draft' | 'pending' | 'confirmed' | 'in_progress' | 'picking' | 'packing' | 'completed' | 'cancelled';
export type WorkOrderPriority = 'low' | 'normal' | 'high' | 'urgent';
export type WorkOrderItemStatus = 'pending' | 'allocated' | 'picking' | 'picked' | 'shipped' | 'cancelled';
export type AllocationStatus = 'allocated' | 'picked' | 'shipped' | 'returned';

export interface WorkOrder {
  id: string;
  work_order_number: string;
  title: string;
  description?: string;
  status: WorkOrderStatus;
  priority: WorkOrderPriority;
  opportunity_id?: string;
  mongo_quote_id?: string;
  account_id?: string;
  account_name?: string;
  supplier_id?: string;
  supplier_name?: string;
  due_date?: string;
  started_at?: string;
  completed_at?: string;
  assigned_to_user_id?: string;
  assigned_to_user_name?: string;
  notes?: string;
  created_by_user_id: string;
  created_by_user_name?: string;
  created_at: string;
  updated_at: string;
  // Computed/joined fields
  item_count?: number;
  total_ordered?: number;
  total_allocated?: number;
  total_picked?: number;
  total_shipped?: number;
  total_value?: number;
}

export interface WorkOrdersResponse {
  items: WorkOrder[];
  page_number: number;
  page_length: number;
  total: number;
}

export interface WorkOrderItem {
  id: string;
  work_order_id: string;
  mongo_sku_id: string;
  sku_code: string;
  sku_name?: string;
  quantity_ordered: number;
  quantity_allocated: number;
  quantity_picked: number;
  quantity_shipped: number;
  unit_price?: number;
  line_total?: number;
  item_status: WorkOrderItemStatus;
  notes?: string;
  sort_order: number;
  created_at: string;
  updated_at: string;
  // Computed fields
  serial_count?: number;
  quantity_remaining?: number;
}

export interface WorkOrderSerial {
  id: string;
  work_order_item_id: string;
  serialized_item_id: string;
  serial_number: string;
  allocation_status: AllocationStatus;
  allocated_at: string;
  allocated_by_user_id?: string;
  picked_at?: string;
  picked_by_user_id?: string;
  shipped_at?: string;
  notes?: string;
  // Joined fields
  sku_code?: string;
  sku_name?: string;
  mac_address?: string;
  dev_id?: string;
  device_status?: DeviceStatus;
  bin_code?: string;
  zone_code?: string;
}

export interface WorkOrderSummary {
  by_status: Array<{
    status: WorkOrderStatus;
    count: number;
  }>;
  by_priority: Array<{
    priority: WorkOrderPriority;
    count: number;
  }>;
  overdue_count: number;
  pending_allocation: {
    item_count: number;
    total_quantity: number;
  };
  recent_orders: Array<{
    id: string;
    work_order_number: string;
    title: string;
    status: WorkOrderStatus;
    priority: WorkOrderPriority;
    account_name?: string;
    due_date?: string;
    created_at: string;
  }>;
}

// =============================================================================
// Work Order Input Types
// =============================================================================

export interface CreateWorkOrderInput {
  title: string;
  description?: string;
  priority?: WorkOrderPriority;
  opportunity_id?: string;
  mongo_quote_id?: string;
  account_id?: string;
  account_name?: string;
  supplier_id?: string;
  supplier_name?: string;
  due_date?: string;
  assigned_to_user_id?: string;
  assigned_to_user_name?: string;
  notes?: string;
}

export interface UpdateWorkOrderInput {
  title?: string;
  description?: string;
  priority?: WorkOrderPriority;
  supplier_id?: string;
  supplier_name?: string;
  due_date?: string;
  assigned_to_user_id?: string;
  assigned_to_user_name?: string;
  notes?: string;
}

export interface AddWorkOrderItemInput {
  mongo_sku_id: string;
  sku_code: string;
  sku_name?: string;
  quantity_ordered?: number;
  unit_price?: number;
  notes?: string;
  sort_order?: number;
}

export interface UpdateWorkOrderItemInput {
  quantity_ordered?: number;
  unit_price?: number;
  notes?: string;
  sort_order?: number;
}

export interface AssignSerialInput {
  item_id: string;
  serialized_item_id: string;
}

export interface CreateFromQuoteInput {
  account_id: string;
  account_name?: string;
}

// =============================================================================
// Work Order Filter Types
// =============================================================================

export interface WorkOrderFilters {
  status?: WorkOrderStatus;
  priority?: WorkOrderPriority;
  account_id?: string;
  supplier_id?: string;
  opportunity_id?: string;
  assigned_to_user_id?: string;
  due_date_from?: string;
  due_date_to?: string;
  search?: string;
  page_number?: number;
  page_length?: number;
  sort_key?: string;
  sort_direction?: 1 | -1;
}

// =============================================================================
// Picking Types
// =============================================================================

export type PickingListStatus = 'pending' | 'assigned' | 'in_progress' | 'completed' | 'cancelled';
export type PickItemStatus = 'pending' | 'picked' | 'partial' | 'short' | 'skipped';
export type ShortageReason = 'not_found' | 'damaged' | 'reserved' | 'wrong_location' | 'count_error' | 'other';

export interface PickingList {
  id: string;
  picking_number: string;
  work_order_id: string;
  work_order_number?: string;
  status: PickingListStatus;
  priority: WorkOrderPriority;
  assigned_to?: string;
  assigned_to_name?: string;
  started_at?: string;
  completed_at?: string;
  notes?: string;
  created_at: string;
  updated_at: string;
  // Computed fields
  item_count?: number;
  items_picked?: number;
  items?: PickingListItem[];
}

export interface PickingListItem {
  id: string;
  picking_list_id: string;
  work_order_item_id: string;
  mongo_sku_id: string;
  sku_code: string;
  sku_name?: string;
  quantity_to_pick: number;
  quantity_picked: number;
  quantity_short: number;
  pick_status: PickItemStatus;
  location_id?: string;
  bin_code?: string;
  zone_code?: string;
  zone_priority?: number;
  lot_number?: string;
  serial_numbers?: string[];
  picked_at?: string;
  picked_by?: string;
  picked_by_name?: string;
  shortage_reason?: ShortageReason;
  notes?: string;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export interface PickingListsResponse {
  items: PickingList[];
  page_number: number;
  page_length: number;
  total: number;
}

export interface PickingSummary {
  by_status: Array<{
    status: PickingListStatus;
    count: number;
  }>;
  today_stats: {
    lists_created: number;
    lists_completed: number;
    items_picked: number;
    shortages: number;
  };
  active_pickers: Array<{
    user_id: string;
    user_name: string;
    active_list_count: number;
    items_picked: number;
  }>;
  recent_lists: PickingList[];
}

// =============================================================================
// Picking Input Types
// =============================================================================

export interface GeneratePickingListInput {
  workOrderId: string;
  priority?: WorkOrderPriority;
  assignedTo?: string;
  assignedToName?: string;
  notes?: string;
}

export interface AssignPickerInput {
  user_id: string;
  user_name?: string;
}

export interface ConfirmPickInput {
  quantity_picked: number;
  lot_number?: string;
  serial_numbers?: string[];
  notes?: string;
}

export interface ReportShortageInput {
  shortage_reason: ShortageReason;
  quantity_short?: number;
  notes?: string;
}

export interface SkipItemInput {
  notes?: string;
}

// =============================================================================
// Picking Filter Types
// =============================================================================

export interface PickingListFilters {
  work_order_id?: string;
  status?: PickingListStatus;
  priority?: WorkOrderPriority;
  assigned_to?: string;
  search?: string;
  page_number?: number;
  page_length?: number;
}

// =============================================================================
// Packing Slip / Shipping Types
// =============================================================================

export type PackingSlipStatus = 'draft' | 'packing' | 'packed' | 'shipped' | 'delivered' | 'cancelled';

export interface PackingSlip {
  id: string;
  slip_number: string;
  work_order_id: string;
  work_order_number?: string;
  picking_list_id?: string;
  picking_number?: string;
  mongo_packing_slip_id?: string;
  mongo_quote_id?: string;
  quote_number?: string;
  status: PackingSlipStatus;
  ship_date?: string;
  shipped_at?: string;
  delivered_at?: string;
  carrier?: string;
  service_type?: string;
  tracking_number?: string;
  tracking_numbers?: string[];
  shipping_cost?: number;
  weight_kg?: number;
  dimensions?: PackageDimensions;
  package_count: number;
  ship_from_site_id?: string;
  ship_from_address?: ShippingAddress;
  ship_to_site_id?: string;
  ship_to_address?: ShippingAddress;
  ship_to_contact?: ContactInfo;
  special_instructions?: string;
  internal_notes?: string;
  prepared_by?: string;
  prepared_by_name?: string;
  prepared_at?: string;
  verified_by?: string;
  verified_by_name?: string;
  verified_at?: string;
  shipped_by?: string;
  shipped_by_name?: string;
  created_by: string;
  created_by_name?: string;
  created_at: string;
  updated_at: string;
  // Joined work order info
  work_order_title?: string;
  work_order_status?: WorkOrderStatus;
  account_name?: string;
  account_id?: string;
  // Items (when loaded)
  items?: PackingSlipItem[];
}

export interface PackingSlipItem {
  id: string;
  packing_slip_id: string;
  work_order_line_id?: string;
  picking_item_id?: string;
  line_number: number;
  mongo_sku_id: string;
  sku_code: string;
  sku_name?: string;
  quantity_ordered: number;
  quantity_packed: number;
  quantity_backordered: number;
  lot_number?: string;
  serial_numbers?: string[];
  unit_weight_kg?: number;
  line_weight_kg?: number;
  package_number: number;
  notes?: string;
  created_at: string;
  updated_at: string;
}

export interface PackageDimensions {
  length: number;
  width: number;
  height: number;
  unit: 'cm' | 'in';
}

export interface ShippingAddress {
  name?: string;
  address_line_1?: string;
  address_line_2?: string;
  city?: string;
  state?: string;
  postal_code?: string;
  country?: string;
  organisation_id?: string;
}

export interface ContactInfo {
  name?: string;
  phone?: string;
  email?: string;
}

export interface PackingSlipsResponse {
  items: PackingSlip[];
  page_number: number;
  page_length: number;
  total: number;
}

export interface ShippingSummary {
  pending_shipments: number;
  shipped_today: number;
  shipped_this_week: number;
  by_status: Array<{
    status: PackingSlipStatus;
    count: number;
  }>;
  by_carrier: Array<{
    carrier: string;
    count: number;
  }>;
  recent_shipments: Array<{
    id: string;
    slip_number: string;
    work_order_number?: string;
    status: PackingSlipStatus;
    carrier?: string;
    tracking_number?: string;
    shipped_at?: string;
  }>;
}

// =============================================================================
// Packing Slip Input Types
// =============================================================================

export interface CreatePackingSlipInput {
  work_order_id: string;
  ship_date?: string;
  ship_to_address?: ShippingAddress;
  ship_to_contact?: ContactInfo;
  special_instructions?: string;
  internal_notes?: string;
}

export interface UpdatePackingSlipInput {
  ship_date?: string;
  carrier?: string;
  service_type?: string;
  tracking_number?: string;
  tracking_numbers?: string[];
  shipping_cost?: number;
  weight_kg?: number;
  dimensions?: PackageDimensions;
  package_count?: number;
  ship_from_site_id?: string;
  ship_from_address?: ShippingAddress;
  ship_to_site_id?: string;
  ship_to_address?: ShippingAddress;
  ship_to_contact?: ContactInfo;
  special_instructions?: string;
  internal_notes?: string;
}

export interface UpdatePackingSlipItemInput {
  quantity_packed?: number;
  quantity_backordered?: number;
  lot_number?: string;
  serial_numbers?: string[];
  unit_weight_kg?: number;
  line_weight_kg?: number;
  package_number?: number;
  notes?: string;
}

export interface ShipPackingSlipInput {
  carrier: string;
  service_type?: string;
  tracking_number: string;
  tracking_numbers?: string[];
  shipping_cost?: number;
  ship_date?: string;
}

// =============================================================================
// Packing Slip Filter Types
// =============================================================================

export interface PackingSlipFilters {
  status?: PackingSlipStatus;
  work_order_id?: string;
  carrier?: string;
  from_date?: string;
  to_date?: string;
  search?: string;
  page_number?: number;
  page_length?: number;
  sort_key?: string;
  sort_direction?: 1 | -1;
}

// Common Carriers
export const CARRIERS = [
  { value: 'fedex', label: 'FedEx', services: ['Ground', 'Express', 'Priority Overnight', '2Day'] },
  { value: 'ups', label: 'UPS', services: ['Ground', '3 Day Select', '2nd Day Air', 'Next Day Air'] },
  { value: 'purolator', label: 'Purolator', services: ['Ground', 'Express', 'Express 9AM'] },
  { value: 'canada_post', label: 'Canada Post', services: ['Regular Parcel', 'Expedited', 'Xpresspost', 'Priority'] },
  { value: 'dhl', label: 'DHL', services: ['Express', 'Express Worldwide'] },
  { value: 'usps', label: 'USPS', services: ['Priority Mail', 'Priority Express', 'Ground Advantage'] },
  { value: 'local_courier', label: 'Local Courier', services: ['Same Day', 'Next Day'] },
  { value: 'customer_pickup', label: 'Customer Pickup', services: [] },
] as const;

// =============================================================================
// Receiving Order Types
// =============================================================================

export type ReceivingOrderStatus = 'draft' | 'in_progress' | 'pending_qc' | 'completed' | 'cancelled';
export type ReceivingLineStatus = 'pending' | 'partial' | 'complete' | 'rejected';
export type QCStatus = 'not_required' | 'pending' | 'passed' | 'failed';

export interface ReceivingOrder {
  id: string;
  order_number: string;
  po_number?: string;
  supplier_name?: string;
  supplier_id?: string;
  status: ReceivingOrderStatus;
  expected_date?: string;
  received_date?: string;
  notes?: string;
  tracking_number?: string;
  carrier_supplier_id?: string;
  carrier?: string;
  payment_method?: string;
  supplier_order_number?: string;
  description?: string;
  purchase_url?: string;
  requester_user_id?: string;
  requester_name?: string;
  total_cost?: number;
  created_by: string;
  created_by_name?: string;
  started_by?: string;
  started_by_name?: string;
  started_at?: string;
  completed_by?: string;
  completed_by_name?: string;
  completed_at?: string;
  cancelled_at?: string;
  cancellation_reason?: string;
  created_at: string;
  updated_at: string;
  // Computed/joined fields
  lines?: ReceivingLine[];
  line_count?: number;
  lines_complete?: number;
  total_lines?: number;  // alias for line_count
  received_lines?: number;  // alias for lines_complete
  total_expected?: number;
  total_received?: number;
}

export type ReceivingDmsLinkKind =
  | 'supplier_invoice'
  | 'packing_slip'
  | 'receiving_report'
  | 'certificate_of_conformance'
  | 'other';

export interface ReceivingDmsLink {
  id: string;
  receivingOrderId: string;
  dmsDocumentId: string;
  kind: ReceivingDmsLinkKind;
  label?: string | null;
  sortOrder: number;
  createdAt?: string;
}

export interface ReceivingLine {
  id: string;
  receiving_order_id: string;
  line_number: number;
  mongo_sku_id?: string;
  sku_code?: string;
  sku_name?: string;
  // Aliases for UI convenience
  sku?: string;  // alias for sku_code
  description?: string;  // alias for sku_name
  unit_of_measure?: string;
  expected_quantity: number;
  received_quantity: number;
  rejected_quantity: number;
  target_location_id?: string;
  actual_location_id?: string;
  lot_number?: string;
  expiration_date?: string;
  unit_cost?: number;
  line_status: ReceivingLineStatus;
  // Alias for UI convenience
  status?: ReceivingLineStatus;  // alias for line_status
  storage_location?: string;  // alias for actual_location_code
  qc_status?: QCStatus;
  qc_notes?: string;
  notes?: string;
  received_at?: string;
  received_by?: string;
  received_by_name?: string;
  created_at: string;
  updated_at: string;
  // Joined location info
  target_location_code?: string;
  target_zone_code?: string;
  actual_location_code?: string;
  actual_zone_code?: string;
  // Serialized items received on this line
  serials?: ReceivingSerial[];
}

export interface ReceivingSerial {
  id: string;
  receiving_line_id: string;
  serial_number: string;
  mac_address?: string;
  dev_id?: string;
  serialized_item_id?: string;
  device_status: DeviceStatus;
  qc_status: QCStatus;
  qc_notes?: string;
  notes?: string;
  created_at: string;
}

export interface ReceivingOrdersResponse {
  items: ReceivingOrder[];
  page_number: number;
  page_length: number;
  total: number;
}

export interface ReceivingSummary {
  total_orders: number;
  by_status: Array<{
    status: ReceivingOrderStatus;
    count: number;
  }>;
  // Convenience counts derived from by_status
  draft_orders?: number;
  in_progress_orders?: number;
  pending_qc_orders?: number;
  completed_orders?: number;
  completed_this_week?: number;
  pending_lines: number;
  items_received_today: number;
  items_received_this_week: number;
  expected_today: number;
  recent_orders: Array<{
    id: string;
    order_number: string;
    po_number?: string;
    supplier_name?: string;
    status: ReceivingOrderStatus;
    expected_date?: string;
    created_at: string;
  }>;
}

// =============================================================================
// Receiving Input Types
// =============================================================================

export interface CreateReceivingOrderInput {
  po_number?: string;
  supplier_name?: string;
  supplier_id?: string;
  expected_date?: string;
  received_date?: string;
  notes?: string;
  tracking_number?: string;
  carrier_supplier_id?: string;
  carrier?: string;
  payment_method?: string;
  supplier_order_number?: string;
  description?: string;
  purchase_url?: string;
  requester_user_id?: string;
}

export interface UpdateReceivingOrderInput {
  po_number?: string;
  supplier_name?: string;
  supplier_id?: string;
  expected_date?: string;
  received_date?: string;
  notes?: string;
  tracking_number?: string;
  carrier_supplier_id?: string | null;
  carrier?: string | null;
  payment_method?: string;
  supplier_order_number?: string;
  description?: string;
  purchase_url?: string;
  requester_user_id?: string | null;
}

export interface AddReceivingLineInput {
  mongo_sku_id?: string;
  sku_code?: string;
  sku_name?: string;
  // Aliases for simplified UI
  sku?: string;  // alias for sku_code
  description?: string;  // alias for sku_name
  unit_of_measure?: string;
  expected_quantity: number;
  target_location_id?: string;
  lot_number?: string;
  expiration_date?: string;
  unit_cost?: number;
  qc_required?: boolean;
  notes?: string;
}

export interface UpdateReceivingLineInput {
  expected_quantity?: number;
  target_location_id?: string;
  lot_number?: string;
  expiration_date?: string;
  unit_cost?: number;
  notes?: string;
}

export interface ReceiveLineInput {
  received_quantity: number;
  rejected_quantity?: number;
  actual_location_id?: string;
  // Alias for simplified UI
  storage_location?: string;  // alias for actual_location_id
  lot_number?: string;
  expiration_date?: string;
  unit_cost?: number;
  qc_required?: boolean;
  notes?: string;
  serials?: Array<{
    serial_number: string;
    mac_address?: string;
    dev_id?: string;
    device_status?: DeviceStatus;
    notes?: string;
  }>;
}

export interface UpdateQCStatusInput {
  qc_status: 'passed' | 'failed';
  qc_notes?: string;
}

export interface CompleteReceivingResult {
  order: ReceivingOrder;
  movements_created: number;
  serials_created: number;
}

// =============================================================================
// Receiving Filter Types
// =============================================================================

export interface ReceivingFilters {
  status?: ReceivingOrderStatus;
  po_number?: string;
  supplier_id?: string;
  supplier_name?: string;
  from_date?: string;
  to_date?: string;
  search?: string;
  page_number?: number;
  page_length?: number;
  sort_key?: string;
  sort_direction?: 1 | -1;
}

// =============================================================================
// User Types (for assignments)
// =============================================================================

export interface WmsUser {
  _id: string;
  email: string;
  firstname?: string;
  lastname?: string;
  username?: string;
  enabled: boolean;
}

export interface WmsUsersResponse {
  users: WmsUser[];
  pageNumber: number;
  pageLength: number;
}

export interface WmsUserFilters {
  search?: string;
  enabled?: boolean;
  page_number?: number;
  page_length?: number;
}

// =============================================================================
// Supplier Types
// =============================================================================

export type SupplierStatus = 'active' | 'inactive' | 'suspended';

export type SupplierKind = 'hardware' | 'software' | 'service' | 'admin' | 'carrier' | 'other';

export const SUPPLIER_KINDS: SupplierKind[] = ['hardware', 'software', 'service', 'admin', 'carrier', 'other'];

export interface Supplier {
  id: string;
  account_number: string;
  name: string;
  description?: string;
  type: 'supplier';
  status: SupplierStatus;
  supplier_kind?: SupplierKind | null;
  hubspot_company_id?: string;
  gdrive_root_folder_id?: string;
  quickbooks_vendor_id?: string | null;
  website?: string | null;
  email?: string | null;
  phone?: string | null;
  sku_count?: number;
  skus?: { id: string; sku: string }[];
  enabled: boolean;
  tags: string[];
  created_at: string;
  updated_at: string;
}

export interface SuppliersResponse {
  suppliers: Supplier[];
  page_number: number;
  page_length: number;
  total: number;
}

export interface SupplierFilters {
  status?: SupplierStatus;
  search?: string;
  kind?: SupplierKind | SupplierKind[];
  tag?: string | string[];
  page_number?: number;
  page_length?: number;
  sort_key?: string;
  sort_direction?: 1 | -1;
}

export interface CreateSupplierInput {
  name: string;
  description?: string;
  status?: SupplierStatus;
  supplier_kind?: SupplierKind;
  hubspot_company_id?: string;
  gdrive_root_folder_id?: string;
  website?: string;
  email?: string;
  phone?: string;
  tags?: string[];
}

export interface UpdateSupplierInput {
  name?: string;
  description?: string;
  status?: SupplierStatus;
  supplier_kind?: SupplierKind | null;
  hubspot_company_id?: string;
  gdrive_root_folder_id?: string;
  website?: string | null;
  email?: string | null;
  phone?: string | null;
  enabled?: boolean;
  tags?: string[];
}

// =============================================================================
// SKU MASTER CATALOG
// =============================================================================

export type SkuType = 'inventory' | 'non_inventory' | 'service';

export type SkuFileType = 'userguide' | 'specsheet' | 'assemblyguide' | 'installationguide' | 'calibrationprocedure';

export interface Sku {
  id: string;
  mongoId?: string;
  organisationId: string;
  sku: string;
  type: SkuType;
  activated: boolean;
  saleable: boolean;
  deleted: boolean;
  nameFr: string;
  nameEn: string;
  searchNameFr?: string;
  searchNameEn?: string;
  descriptionFr?: string;
  descriptionEn?: string;
  manufacturerId?: string;
  manufacturerName?: string;
  model?: string;
  weight?: string;
  value?: string;
  cost?: string;
  inventory: number;
  minstock?: number;
  gdriveLink?: string;
  atekNotes?: string;
  tags?: string[];
  quickbookId?: string;
  medusaId?: string;
  hubspotId?: string;
  createdAt: string;
  updatedAt: string;
  bom?: SkuBomItem[];
  suppliers?: SkuSupplier[];
  files?: SkuFile[];
  photos?: SkuPhoto[];
  locations?: SkuLocation[];
  dmsLinks?: SkuDmsLink[];
}

export type SkuDmsLinkKind = 'user_manual' | 'datasheet' | 'certificate' | 'app_note' | 'other';

export interface SkuDmsLink {
  id: string;
  skuId: string;
  dmsDocumentId: string;
  kind: SkuDmsLinkKind;
  label?: string | null;
  sortOrder: number;
  createdAt?: string;
}

export interface SkuBomItem {
  id: string;
  componentSkuId: string;
  qty: number;
  componentSku?: string;
  componentNameFr?: string;
  componentNameEn?: string;
}

export interface SkuSupplier {
  id: string;
  organisationId: string;
  organisationName?: string;
  supplierSku?: string;
  link?: string;
  price?: string;
  leadTimeDays?: number;
  moq?: number;
}

export interface SkuFile {
  id: string;
  fileType: SkuFileType;
  mongoFileId?: string;
  filename?: string;
  url?: string;
}

export interface SkuPhoto {
  id: string;
  skuId?: string;
  mongoFileId?: string;
  url?: string;
  originalFilename?: string;
  mimeType?: string;
  sizeBytes?: number;
  storagePath?: string;
  uploadedById?: string;
  uploadedByName?: string;
  sortOrder: number;
  createdAt?: string;
}

export interface SkuLocation {
  skuId: string;
  locationId: string;
}

export interface SkuFilters {
  /** 'true' = activated only, 'false' = archived only, undefined = all */
  activated?: string;
}

export interface SkusResponse {
  skus: Sku[];
  total: number;
  pageNumber: number;
  pageLength: number;
}

export interface CreateSkuInput {
  sku: string;
  type: SkuType;
  nameFr: string;
  nameEn: string;
  activated?: boolean;
  saleable?: boolean;
  descriptionFr?: string;
  descriptionEn?: string;
  manufacturerId?: string;
  manufacturerName?: string;
  model?: string;
  weight?: number;
  value?: number;
  cost?: number;
  inventory?: number;
  minstock?: number;
  gdriveLink?: string;
  atekNotes?: string;
  tags?: string[];
  quickbookId?: string;
  medusaId?: string;
  hubspotId?: string;
}

export interface UpdateSkuInput extends Partial<CreateSkuInput> {}

export interface SkuSearchResult {
  id: string;
  sku: string;
  nameFr: string;
  nameEn: string;
  type: SkuType;
  manufacturerName?: string;
  model?: string;
  value?: string;
}
